import { daysBetween, formatShortDate, relativeDay, type ISODate } from '@app/shared'

import type { SearchDomain, SearchHitKind } from '../../lib/data'

// ============================================================================
// SEARCH · THE PHRASING LAYER — docs/design/handoff/"LifeOS Search.dc.html".
//
// THE DATA CARRIES FACTS, THIS FILE CARRIES WORDS (README §State Management:
// derived, never stored). `SearchHit.date` is an ISO date; whether that date
// reads as 'saved 3d', 'due Sep 8', 'extracted Aug 3' or 'today' depends on what
// kind of thing it is, and that is a rendering decision — so it lives here,
// against an explicit `today`, never against the wall clock. Nothing in this
// file calls Date.now(); demo mode would drift and every future screenshot
// baseline would fail on every run.
// ============================================================================

/** The badge over every result row. One label per kind — the mockup's own strings. */
export const KIND_LABEL: Record<SearchHitKind, string> = {
  journal_entry: 'JOURNAL',
  voice_memo: 'JOURNAL · VOICE MEMO',
  transcript: 'SCHOOL · TRANSCRIPT',
  announcement: 'SCHOOL · ANNOUNCEMENT',
  syllabus: 'SCHOOL · SYLLABUS',
  // The mockup draws no assignment, summary, learning-item or task-list badge; these four follow
  // the pattern of the ones it does draw (DOMAIN · TYPE, 600 9.5px, 0.04em) rather than inventing
  // a second grammar. Flagged in the port report as an extension, not a transcription.
  assignment: 'SCHOOL · DEADLINE',
  lecture_summary: 'SCHOOL · LECTURE SUMMARY',
  // A file bCourses holds under a course — a syllabus PDF, a slide deck. 'FILE' and not 'PDF':
  // the catalog stores whatever Canvas lists, and a badge that promised a PDF would be wrong the
  // first time an instructor posts a .docx.
  course_file: 'SCHOOL · FILE',
  save_reddit: 'INTERESTS · REDDIT',
  save_youtube: 'INTERESTS · YOUTUBE',
  save_article: 'INTERESTS · ARTICLE',
  save_x: 'INTERESTS · X',
  task: 'TASK',
  learning_item: 'LEARNING',
  learning_session: 'LEARNING · SESSION NOTE',
}

/** The five filter chips, in the mockup's order. */
export const DOMAIN_LABEL: Record<SearchDomain, string> = {
  journal: 'Journal',
  school: 'School',
  interests: 'Interests',
  // The chip reads Reminders because the screen it filters to does. The KEY is still `tasks` —
  // it is the API's own domain name (`/api/search?domain=tasks`), and a label is not a contract.
  tasks: 'Reminders',
  learning: 'Learning',
}

/**
 * 'saved 3d' / 'saved 2w' / 'saved 1mo'. Coarse ON PURPOSE: the demo corpus stores a save's day,
 * not its hour, so anything finer than a day would be a number the app does not have.
 */
function agoShort(date: ISODate, today: ISODate): string {
  const days = daysBetween(date, today)
  if (days <= 0) return 'today'
  if (days === 1) return '1d'
  if (days < 7) return `${days}d`
  if (days < 30) return `${Math.round(days / 7)}w`
  if (days < 365) return `${Math.round(days / 30)}mo`
  return `${Math.round(days / 365)}y`
}

/**
 * The right-aligned date on a result row. What the date MEANS differs by kind — a save's date is
 * when it arrived, a deadline's is when it is due, a syllabus fact's is when a model read it out
 * of a PDF — and printing four different facts as one bare 'Aug 12' would flatten that away.
 */
export function dateLabel(kind: SearchHitKind, date: ISODate | null, today: ISODate): string | null {
  if (date === null) return null
  switch (kind) {
    case 'save_reddit':
    case 'save_youtube':
    case 'save_article':
    case 'save_x':
      return `saved ${agoShort(date, today)}`
    case 'assignment':
    case 'task':
      return `due ${formatShortDate(date)}`
    case 'syllabus':
      return `extracted ${formatShortDate(date)}`
    case 'announcement':
      return relativeDay(date, today)
    default:
      return formatShortDate(date)
  }
}

/** '2 other matches in this entry' — the mockup's line, pluralised, and only when it is true. */
export function otherMatchesLine(count: number, kind: SearchHitKind): string | null {
  if (count <= 0) return null
  const noun =
    kind === 'journal_entry'
      ? 'entry'
      : kind === 'transcript'
        ? 'transcript'
        : kind === 'voice_memo'
          ? 'memo'
          : 'item'
  return `${count} other match${count === 1 ? '' : 'es'} in this ${noun}`
}

/** The header's count line: '14 results', or '2 exact · 5 related' once there is a second block. */
export function resultsLine(exact: number, related: number): string {
  if (related > 0) return `${exact} exact · ${related} related`
  return `${exact} result${exact === 1 ? '' : 's'}`
}

const SMALL = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine']

/**
 * The line under EXACT MATCHES. Both forms are the mockup's own: '14 items · ranked by relevance,
 * not grouped by type' for a single word, and '2 items contain all four words' for a phrase —
 * where "four" is SPELLED OUT, as the handoff writes it. Copy is data (FIDELITY rule 4).
 */
export function exactBlockNote(items: number, terms: number): string {
  const noun = `${items} item${items === 1 ? '' : 's'}`
  if (terms > 1) return `${noun} contain all ${SMALL[terms] ?? terms} words`
  return `${noun} · ranked by relevance, not grouped by type`
}
